import { xchacha20poly1305 } from "@noble/ciphers/chacha.js";
import { ed25519, x25519 } from "@noble/curves/ed25519.js";
import { hkdf } from "@noble/hashes/hkdf.js";
import { sha256 } from "@noble/hashes/sha2.js";
import { entropyToMnemonic, mnemonicToEntropy, validateMnemonic } from "@scure/bip39";
import { wordlist } from "@scure/bip39/wordlists/english";
import { ChannelPassSchema, ChannelProofSchema, InviteConsumptionSchema, InviteRejectionSchema, InvitePayloadSchema, PROTOCOL_VERSION, RoleCertificateSchema, RoleRevocationSchema, PushWakeGrantSchema, PushWakeRevocationSchema, PushWakeSchema, SignedInviteSchema, SignedOperationSchema, SyncEnvelopeSchema, } from "./schemas.js";
import { base64UrlToBytes, bytesToBase64Url, canonicalJson, createId, decoder, encoder, } from "./utils.js";
const DOMAIN_SALT = encoder.encode("palestra.local-first/v1");
function randomBytes(length) {
    return crypto.getRandomValues(new Uint8Array(length));
}
export function deriveIdentity(entropy) {
    if (entropy.length !== 16)
        throw new Error("Identity entropy must contain 128 bits");
    const signingPrivateKey = hkdf(sha256, entropy, DOMAIN_SALT, "signing/ed25519", 32);
    const encryptionPrivateKey = hkdf(sha256, entropy, DOMAIN_SALT, "encryption/x25519", 32);
    const signingPublicKey = ed25519.getPublicKey(signingPrivateKey);
    const encryptionPublicKey = x25519.getPublicKey(encryptionPrivateKey);
    return {
        id: identityIdFromSigningKey(bytesToBase64Url(signingPublicKey)),
        entropy: bytesToBase64Url(entropy),
        signingPrivateKey: bytesToBase64Url(signingPrivateKey),
        signingPublicKey: bytesToBase64Url(signingPublicKey),
        encryptionPrivateKey: bytesToBase64Url(encryptionPrivateKey),
        encryptionPublicKey: bytesToBase64Url(encryptionPublicKey),
    };
}
export function identityIdFromSigningKey(signingPublicKey) {
    return `person_${bytesToBase64Url(sha256(base64UrlToBytes(signingPublicKey))).slice(0, 24)}`;
}
export function createIdentity() {
    return deriveIdentity(randomBytes(16));
}
export function identityToMnemonic(identity) {
    return entropyToMnemonic(base64UrlToBytes(identity.entropy), wordlist);
}
export function identityFromMnemonic(mnemonic) {
    const normalized = mnemonic.trim().toLowerCase().replace(/\s+/gu, " ");
    if (!validateMnemonic(normalized, wordlist))
        throw new Error("Invalid recovery phrase");
    return deriveIdentity(mnemonicToEntropy(normalized, wordlist));
}
export function toPublicIdentity(identity) {
    return {
        id: identity.id,
        signingPublicKey: identity.signingPublicKey,
        encryptionPublicKey: identity.encryptionPublicKey,
    };
}
export function signValue(value, privateKey) {
    return bytesToBase64Url(ed25519.sign(encoder.encode(canonicalJson(value)), base64UrlToBytes(privateKey)));
}
export function verifyValue(value, signature, publicKey) {
    try {
        return ed25519.verify(base64UrlToBytes(signature), encoder.encode(canonicalJson(value)), base64UrlToBytes(publicKey));
    }
    catch {
        return false;
    }
}
export function createSignedOperation(input) {
    const unsigned = {
        protocolVersion: PROTOCOL_VERSION,
        id: createId("op"),
        gymId: input.gymId,
        authorId: input.identity.id,
        authorRole: input.role,
        createdAt: input.now ?? new Date().toISOString(),
        parents: input.parents ?? [],
        kind: input.kind,
        payload: input.payload,
    };
    return SignedOperationSchema.parse({
        ...unsigned,
        signature: signValue(unsigned, input.identity.signingPrivateKey),
    });
}
export function verifySignedOperation(operation, authorSigningKey, expectedGymId) {
    const parsed = SignedOperationSchema.safeParse(operation);
    if (!parsed.success || operation.gymId !== expectedGymId)
        return false;
    const { signature, ...unsigned } = operation;
    return verifyValue(unsigned, signature, authorSigningKey);
}
export function createInvite(input) {
    const issuedAt = input.now ?? new Date().toISOString();
    const payload = InvitePayloadSchema.parse({
        protocolVersion: PROTOCOL_VERSION,
        id: createId("invite"),
        gymId: input.gymId,
        gymName: input.gymName,
        channelId: input.channelId,
        role: input.role,
        issuedBy: input.issuer.id,
        issuedAt,
        expiresAt: input.expiresAt,
        nonce: bytesToBase64Url(randomBytes(18)),
    });
    return SignedInviteSchema.parse({
        payload,
        issuerSigningKey: input.issuer.signingPublicKey,
        issuerEncryptionKey: input.issuer.encryptionPublicKey,
        /**
         * Il lasciapassare dell'invitato dura quanto l'invito: e' questo a rendere
         * inutile, per il relay, un invito scaduto rimasto in mano a un estraneo.
         * E non estende la catena — `canIssue: false` anche per un coach: essere
         * invitati non conferisce il potere di invitare, che e' del solo fondatore.
         */
        channelProof: [
            createChannelPass({
                issuer: input.issuer,
                channelId: input.channelId,
                canIssue: false,
                expiresAt: input.expiresAt,
                now: issuedAt,
            }),
        ],
        signature: signValue(payload, input.issuer.signingPrivateKey),
    });
}
export function verifyInvite(invite, now = new Date().toISOString()) {
    const parsed = SignedInviteSchema.safeParse(invite);
    if (!parsed.success || Date.parse(invite.payload.expiresAt) <= Date.parse(now))
        return false;
    if (!verifyValue(invite.payload, invite.signature, invite.issuerSigningKey))
        return false;
    // Un invito il cui lasciapassare non risale al fondatore e' un invito che non
    // apre nessun canale: meglio dirlo qui che lasciarlo scoprire al relay.
    return verifyInviteChannelProof(invite, now);
}
export function encodeInvite(invite) {
    return bytesToBase64Url(encoder.encode(JSON.stringify(invite)));
}
export function decodeInvite(encoded) {
    return SignedInviteSchema.parse(JSON.parse(decoder.decode(base64UrlToBytes(encoded))));
}
/**
 * Il link corto: l'invito non viaggia piu' nell'URL ma in una cassetta sul
 * relay, cifrato con una chiave che il relay non vede mai.
 *
 *   https://palestra.esempio.it/i/K7fVq2mXnP1s#8bC3dEfGhJkLmNoPqRsTu
 *   └──────── origine ────────┘└─ token ───┘ └────── chiave ──────┘
 *                                 va al relay     non lascia mai il browser
 *
 * Il frammento — tutto cio' che segue `#` — non viene inviato al server in
 * nessuna richiesta HTTP: e' una proprieta' del protocollo, non una convenzione.
 */
export const INVITE_LINK_TOKEN_BYTES = 9;
export const INVITE_LINK_KEY_BYTES = 16;
const INVITE_LINK_PATH_PATTERN = /^\/i\/([A-Za-z0-9_-]{12})\/?$/u;
const INVITE_LINK_KEY_PATTERN = /^[A-Za-z0-9_-]{22}$/u;
/**
 * La chiave del blob non e' `k` direttamente ma una derivazione: lo stesso
 * schema degli envelope e del backup. Il token entra come dato autenticato,
 * cosi' un blob spostato su un'altra cassetta non supera il Poly1305.
 */
function inviteLinkCipher(key, token, nonce) {
    const material = hkdf(sha256, base64UrlToBytes(key), DOMAIN_SALT, "invite-link/xchacha20", 32);
    return xchacha20poly1305(material, nonce, encoder.encode(token));
}
/**
 * Comprime e cifra un invito per il link corto. La crittografia dell'invito,
 * la sua firma e la catena di lasciapassare non cambiano: cambia solo il mezzo
 * con cui l'invito arriva a destinazione.
 */
export async function sealInviteLink(invite) {
    const token = bytesToBase64Url(randomBytes(INVITE_LINK_TOKEN_BYTES));
    const key = bytesToBase64Url(randomBytes(INVITE_LINK_KEY_BYTES));
    const plain = await gzipCompress(encoder.encode(JSON.stringify(SignedInviteSchema.parse(invite))));
    const nonce = randomBytes(24);
    const sealed = inviteLinkCipher(key, token, nonce).encrypt(plain);
    const combined = new Uint8Array(nonce.length + sealed.length);
    combined.set(nonce);
    combined.set(sealed, nonce.length);
    return { token, key, blob: bytesToBase64Url(combined) };
}
/**
 * Decifra e decomprime il blob riscattato da `GET /l/<token>`. Da qui in poi il
 * flusso e' quello di sempre: `verifyInvite`, creazione dell'identita',
 * `sync.requested` — nessuna verifica viene indebolita o saltata.
 */
export async function openInviteLink(sealed) {
    const combined = base64UrlToBytes(sealed.blob);
    if (combined.length <= 24)
        throw new Error("Invite link blob is malformed");
    const plain = inviteLinkCipher(sealed.key, sealed.token, combined.slice(0, 24)).decrypt(combined.slice(24));
    return SignedInviteSchema.parse(JSON.parse(decoder.decode(await gzipDecompress(plain))));
}
export function formatInviteLinkUrl(origin, link) {
    return `${origin.replace(/\/+$/u, "")}/i/${link.token}#${link.key}`;
}
/**
 * Token dal percorso, chiave dal frammento. `undefined` per qualsiasi cosa non
 * abbia esattamente questa forma: un percorso diverso non e' un errore, e' solo
 * un'altra pagina.
 */
export function parseInviteLinkLocation(location) {
    const match = INVITE_LINK_PATH_PATTERN.exec(location.pathname);
    if (!match)
        return undefined;
    const key = location.hash.startsWith("#") ? location.hash.slice(1) : location.hash;
    if (!INVITE_LINK_KEY_PATTERN.test(key))
        return undefined;
    return { token: match[1], key };
}
/**
 * gzip via gli stream standard, presenti nei browser che l'app gia' richiede e
 * in Node ≥ 18. Il fallback senza compressione esiste per gli ambienti di test
 * che non li espongono: il gzip si riconosce dai primi due byte (0x1f 0x8b),
 * mentre un JSON comincia con `{`, quindi le due forme non si confondono.
 */
async function gzipCompress(bytes) {
    if (typeof CompressionStream === "undefined")
        return bytes;
    return transformBytes(bytes, new CompressionStream("gzip"));
}
async function gzipDecompress(bytes) {
    if (bytes[0] !== 0x1f || bytes[1] !== 0x8b)
        return bytes;
    if (typeof DecompressionStream === "undefined") {
        throw new Error("Invite link requires DecompressionStream");
    }
    return transformBytes(bytes, new DecompressionStream("gzip"));
}
async function transformBytes(bytes, transform) {
    const writer = transform.writable.getWriter();
    // La copia fissa il buffer su un ArrayBuffer proprio: gli stream non
    // accettano viste su memoria condivisa, e il chiamante non deve saperlo.
    const writing = writer.write(new Uint8Array(bytes)).then(() => writer.close());
    const chunks = [];
    const reader = transform.readable.getReader();
    for (;;) {
        const { done, value } = await reader.read();
        if (done)
            break;
        chunks.push(value);
    }
    await writing;
    const combined = new Uint8Array(chunks.reduce((total, chunk) => total + chunk.length, 0));
    let offset = 0;
    for (const chunk of chunks) {
        combined.set(chunk, offset);
        offset += chunk.length;
    }
    return combined;
}
export function issueRoleCertificate(input) {
    const unsigned = {
        schemaVersion: 1,
        id: createId("role"),
        gymId: input.gymId,
        subjectId: input.subject.id,
        subjectSigningKey: input.subject.signingPublicKey,
        subjectEncryptionKey: input.subject.encryptionPublicKey,
        role: input.role,
        issuedBy: input.issuer.id,
        issuedAt: input.now ?? new Date().toISOString(),
        ...(input.expiresAt ? { expiresAt: input.expiresAt } : {}),
    };
    return RoleCertificateSchema.parse({
        ...unsigned,
        signature: signValue(unsigned, input.issuer.signingPrivateKey),
    });
}
export function verifyRoleCertificate(certificate, issuerSigningKey, now = new Date().toISOString()) {
    const parsed = RoleCertificateSchema.safeParse(certificate);
    if (!parsed.success || certificate.revokedAt)
        return false;
    if (certificate.expiresAt && Date.parse(certificate.expiresAt) <= Date.parse(now))
        return false;
    const { signature, ...unsigned } = certificate;
    return verifyValue(unsigned, signature, issuerSigningKey);
}
export function createRoleRevocation(input) {
    const unsigned = {
        schemaVersion: 1,
        id: createId("revocation"),
        gymId: input.gymId,
        subjectId: input.subjectId,
        revokedBy: input.owner.id,
        revokedAt: input.now ?? new Date().toISOString(),
    };
    return RoleRevocationSchema.parse({
        ...unsigned,
        signature: signValue(unsigned, input.owner.signingPrivateKey),
    });
}
export function verifyRoleRevocation(revocation, ownerSigningKey) {
    const parsed = RoleRevocationSchema.safeParse(revocation);
    if (!parsed.success)
        return false;
    const { signature, ...unsigned } = revocation;
    return verifyValue(unsigned, signature, ownerSigningKey);
}
/** Emette il permesso relay-readable con cui un coach può svegliare gli atleti. */
export function createPushWakeGrant(input) {
    const unsigned = {
        schemaVersion: 1,
        id: createId("push_grant"),
        channelId: input.channelId,
        subjectId: input.subjectId,
        permission: "broadcast",
        issuerSigningKey: input.owner.signingPublicKey,
        issuedAt: input.now ?? new Date().toISOString(),
        expiresAt: input.expiresAt,
    };
    return PushWakeGrantSchema.parse({
        ...unsigned,
        signature: signValue(unsigned, input.owner.signingPrivateKey),
    });
}
export function verifyPushWakeGrant(grant, now = new Date().toISOString()) {
    const parsed = PushWakeGrantSchema.safeParse(grant);
    if (!parsed.success ||
        Date.parse(grant.expiresAt) <= Date.parse(now) ||
        channelIdForOwner(grant.issuerSigningKey) !== grant.channelId)
        return false;
    const { signature, ...unsigned } = grant;
    return verifyValue(unsigned, signature, grant.issuerSigningKey);
}
export function createPushWakeRevocation(input) {
    const unsigned = {
        schemaVersion: 1,
        id: createId("push_revoke"),
        channelId: input.channelId,
        subjectId: input.subjectId,
        revokedAt: input.now ?? new Date().toISOString(),
        issuerSigningKey: input.owner.signingPublicKey,
    };
    return PushWakeRevocationSchema.parse({
        ...unsigned,
        signature: signValue(unsigned, input.owner.signingPrivateKey),
    });
}
export function verifyPushWakeRevocation(revocation) {
    const parsed = PushWakeRevocationSchema.safeParse(revocation);
    if (!parsed.success || channelIdForOwner(revocation.issuerSigningKey) !== revocation.channelId)
        return false;
    const { signature, ...unsigned } = revocation;
    return verifyValue(unsigned, signature, revocation.issuerSigningKey);
}
export function createPushWake(input) {
    const unsigned = {
        schemaVersion: 1,
        channelId: input.channelId,
        envelopeId: input.envelopeId,
        senderId: input.identity.id,
        recipientIds: [...new Set(input.recipientIds)],
        createdAt: input.now ?? new Date().toISOString(),
        ...(input.grant ? { grant: input.grant } : {}),
    };
    return PushWakeSchema.parse({
        ...unsigned,
        signature: signValue(unsigned, input.identity.signingPrivateKey),
    });
}
export function verifyPushWake(wake, senderSigningKey) {
    const parsed = PushWakeSchema.safeParse(wake);
    if (!parsed.success)
        return false;
    const { signature, ...unsigned } = wake;
    return verifyValue(unsigned, signature, senderSigningKey);
}
/**
 * Registra che un invito e' stato speso — o che viene ritirato senza essere
 * stato speso, se `consumedBy` non c'e'. Lo firma chi ha visto entrare il nuovo
 * arrivato, cioe' un membro dello staff: e' lui a poterlo dire agli altri.
 */
export function recordInviteConsumption(input) {
    const unsigned = {
        schemaVersion: 1,
        id: createId("spent"),
        gymId: input.gymId,
        inviteId: input.inviteId,
        ...(input.consumedBy ? { consumedBy: input.consumedBy } : {}),
        recordedBy: input.recorder.id,
        recordedAt: input.now ?? new Date().toISOString(),
    };
    return InviteConsumptionSchema.parse({
        ...unsigned,
        signature: signValue(unsigned, input.recorder.signingPrivateKey),
    });
}
/**
 * Il rifiuto che chiude il silenzio: lo firma chi scarta l'operazione, ed e'
 * l'unica cosa che chi ha presentato un invito di un altro possa ricevere da
 * questa palestra. Non concede nulla — non e' un certificato, non apre niente
 * — e per questo puo' essere mandato a uno sconosciuto senza pensarci due
 * volte.
 */
export function recordInviteRejection(input) {
    const unsigned = {
        schemaVersion: 1,
        id: createId("rejected"),
        gymId: input.gymId,
        inviteId: input.inviteId,
        subjectId: input.subjectId,
        reason: input.reason,
        rejectedBy: input.rejecter.id,
        rejectedAt: input.now ?? new Date().toISOString(),
    };
    return InviteRejectionSchema.parse({
        ...unsigned,
        signature: signValue(unsigned, input.rejecter.signingPrivateKey),
    });
}
export function verifyInviteRejection(rejection, rejecterSigningKey) {
    const parsed = InviteRejectionSchema.safeParse(rejection);
    if (!parsed.success)
        return false;
    const { signature, ...unsigned } = rejection;
    return verifyValue(unsigned, signature, rejecterSigningKey);
}
export function verifyInviteConsumption(consumption, recorderSigningKey) {
    const parsed = InviteConsumptionSchema.safeParse(consumption);
    if (!parsed.success)
        return false;
    const { signature, ...unsigned } = consumption;
    return verifyValue(unsigned, signature, recorderSigningKey);
}
function wrapDocumentKey(documentKey, recipient) {
    const ephemeralPrivateKey = randomBytes(32);
    const ephemeralPublicKey = x25519.getPublicKey(ephemeralPrivateKey);
    const sharedSecret = x25519.getSharedSecret(ephemeralPrivateKey, base64UrlToBytes(recipient.encryptionPublicKey));
    const wrappingKey = hkdf(sha256, sharedSecret, DOMAIN_SALT, `wrap/${recipient.id}`, 32);
    const nonce = randomBytes(24);
    const wrapped = xchacha20poly1305(wrappingKey, nonce, encoder.encode(recipient.id)).encrypt(documentKey);
    const combined = new Uint8Array(nonce.length + wrapped.length);
    combined.set(nonce);
    combined.set(wrapped, nonce.length);
    return {
        recipientId: recipient.id,
        ephemeralPublicKey: bytesToBase64Url(ephemeralPublicKey),
        wrappedKey: bytesToBase64Url(combined),
    };
}
function unwrapDocumentKey(recipient, identity) {
    const combined = base64UrlToBytes(recipient.wrappedKey);
    const nonce = combined.slice(0, 24);
    const ciphertext = combined.slice(24);
    const sharedSecret = x25519.getSharedSecret(base64UrlToBytes(identity.encryptionPrivateKey), base64UrlToBytes(recipient.ephemeralPublicKey));
    const wrappingKey = hkdf(sha256, sharedSecret, DOMAIN_SALT, `wrap/${identity.id}`, 32);
    return xchacha20poly1305(wrappingKey, nonce, encoder.encode(identity.id)).decrypt(ciphertext);
}
export function opaqueChannelId(gymId) {
    return `channel_${bytesToBase64Url(sha256(encoder.encode(`gym/${gymId}`))).slice(0, 32)}`;
}
/**
 * Il canale di una palestra e' derivato dalla chiave del fondatore, non dal
 * `gymId`. E' cio' che rende verificabile l'appartenenza: il relay, che il
 * `gymId` non lo conosce e non deve conoscerlo, puo' comunque stabilire da solo
 * se chi firma un lasciapassare e' l'ancora di quel canale o un estraneo.
 *
 * Un'identita' ancora un canale solo: una persona che fondasse due palestre
 * dovrebbe farlo con due identita' — cioe' con due profili — come gia' oggi.
 */
export function channelIdForOwner(ownerSigningPublicKey) {
    return `channel_${bytesToBase64Url(sha256(encoder.encode(`channel/owner/${ownerSigningPublicKey}`))).slice(0, 32)}`;
}
/**
 * La casella personale in cui vive il backup cifrato. Non e' un canale di
 * palestra e non richiede lasciapassare: il relay la ricalcola dall'identita'
 * che ha appena superato la challenge, quindi nessuno puo' presentarsi alla
 * casella di un altro. Serve al rientro con le sole 12 parole, quando la
 * palestra non si sa ancora quale sia.
 */
export function recoveryChannelId(identityId) {
    return opaqueChannelId(`recovery/${identityId}`);
}
/**
 * Quanto puo' durare al massimo un lasciapassare al portatore. L'invito decide
 * la propria scadenza, ma qui c'e' il tetto: senza, chi invita potrebbe
 * emettere un pass valido per anni e riaprire da solo il canale a chiunque.
 */
export const MAX_BEARER_PASS_MS = 30 * 24 * 60 * 60 * 1000;
export function createChannelPass(input) {
    const unsigned = {
        channelId: input.channelId,
        ...(input.subjectId ? { subjectId: input.subjectId } : {}),
        canIssue: input.canIssue,
        issuerId: input.issuer.id,
        issuerSigningKey: input.issuer.signingPublicKey,
        issuedAt: input.now ?? new Date().toISOString(),
        ...(input.expiresAt ? { expiresAt: input.expiresAt } : {}),
    };
    return ChannelPassSchema.parse({
        ...unsigned,
        signature: signValue(unsigned, input.issuer.signingPrivateKey),
    });
}
function channelPassValid(pass, channelId, nowMs) {
    if (pass.channelId !== channelId)
        return false;
    // L'identita' di chi ammette non e' una dichiarazione: e' l'impronta della
    // chiave con cui la firma viene verificata.
    if (identityIdFromSigningKey(pass.issuerSigningKey) !== pass.issuerId)
        return false;
    if (pass.expiresAt && Date.parse(pass.expiresAt) <= nowMs)
        return false;
    if (!pass.subjectId) {
        // Al portatore: deve scadere, e non oltre il tetto.
        if (!pass.expiresAt)
            return false;
        if (Date.parse(pass.expiresAt) - Date.parse(pass.issuedAt) > MAX_BEARER_PASS_MS)
            return false;
    }
    const { signature, ...unsigned } = pass;
    return verifyValue(unsigned, signature, pass.issuerSigningKey);
}
/**
 * La domanda che il relay si pone prima di lasciar entrare qualcuno in un
 * canale: «questa identita' e' stata ammessa da chi poteva ammetterla?».
 *
 * Tre risposte affermative, e nessun'altra:
 * 1. e' la propria casella personale, ricalcolabile dall'identita';
 * 2. e' il fondatore del canale, perche' il canale e' derivato dalla sua chiave;
 * 3. porta una catena di lasciapassare che risale fino a quella chiave.
 *
 * Non richiede alcuno stato sul relay e non gli rivela nulla della palestra:
 * tutto quello che serve e' dentro la catena, e la catena e' firmata.
 */
export function verifyChannelAdmission(input) {
    const { channelId, clientId, clientSigningKey } = input;
    if (identityIdFromSigningKey(clientSigningKey) !== clientId)
        return false;
    if (recoveryChannelId(clientId) === channelId)
        return true;
    if (channelIdForOwner(clientSigningKey) === channelId)
        return true;
    const leaf = verifiedChannelChainLeaf(input.proof, channelId, input.now);
    if (!leaf)
        return false;
    // Al portatore vale per chiunque lo presenti; intestato, per una persona sola.
    return leaf.subjectId === undefined || leaf.subjectId === clientId;
}
/**
 * La catena, verificata dall'ancora in giu', senza ancora chiedersi *chi* la
 * stia presentando: restituisce l'ultimo anello, cioe' quello che dice a chi il
 * canale e' stato aperto. `undefined` significa catena non valida.
 */
function verifiedChannelChainLeaf(candidate, channelId, now) {
    const parsed = ChannelProofSchema.safeParse(candidate);
    if (!parsed.success)
        return undefined;
    const proof = parsed.data;
    // La radice della catena e' l'ancora del canale, non un'identita' qualunque.
    if (channelIdForOwner(proof[0].issuerSigningKey) !== channelId)
        return undefined;
    const nowMs = Date.parse(now ?? new Date().toISOString());
    for (const [index, pass] of proof.entries()) {
        if (!channelPassValid(pass, channelId, nowMs))
            return undefined;
        const next = proof[index + 1];
        if (!next)
            return pass;
        // Un anello intermedio deve nominare qualcuno e poter ammettere.
        if (!pass.canIssue || !pass.subjectId || next.issuerId !== pass.subjectId)
            return undefined;
    }
    return undefined;
}
/**
 * Il lasciapassare che un invito porta con se': deve essere al portatore — chi
 * invita non conosce ancora l'identita' dell'invitato — e valere per il canale
 * che l'invito dichiara, quello firmato.
 *
 * E dev'essere un anello solo. Un invito nasce dall'ancora del canale e da
 * nessun altro: piu' anelli significano un intermediario, e un intermediario
 * che invita e' esattamente cio' che non deve esistere. Il vincolo sta qui,
 * dentro `verifyInvite`, cioe' dalla parte di chi riceve: un client modificato
 * puo' firmare quello che vuole, ma nessuno glielo accetta.
 */
export function verifyInviteChannelProof(invite, now) {
    if (invite.channelProof.length !== 1)
        return false;
    const leaf = verifiedChannelChainLeaf(invite.channelProof, invite.payload.channelId, now);
    return (leaf !== undefined && leaf.subjectId === undefined && leaf.issuerId === invite.payload.issuedBy);
}
/**
 * Deriva dalla sola entropia: il backup deve tornare leggibile da un dispositivo
 * che possiede unicamente le 12 parole. Non riusa le coppie di chiavi esistenti,
 * cosi' la custodia del backup resta indipendente da firma e cifratura fra pari.
 */
function backupKey(identity) {
    return hkdf(sha256, base64UrlToBytes(identity.entropy), DOMAIN_SALT, "backup/xchacha20", 32);
}
export function encryptProfileBackup(identity, payload) {
    const nonce = randomBytes(24);
    // L'identita' come dato autenticato: un blob di un altro proprietario non
    // supera il Poly1305 nemmeno se la chiave coincidesse.
    const ciphertext = xchacha20poly1305(backupKey(identity), nonce, encoder.encode(identity.id)).encrypt(encoder.encode(canonicalJson(payload)));
    const combined = new Uint8Array(nonce.length + ciphertext.length);
    combined.set(nonce);
    combined.set(ciphertext, nonce.length);
    return bytesToBase64Url(combined);
}
export function decryptProfileBackup(identity, blob) {
    const combined = base64UrlToBytes(blob);
    if (combined.length <= 24)
        throw new Error("Profile backup is malformed");
    const plaintext = xchacha20poly1305(backupKey(identity), combined.slice(0, 24), encoder.encode(identity.id)).decrypt(combined.slice(24));
    return JSON.parse(decoder.decode(plaintext));
}
export function encryptEnvelope(input) {
    if (input.recipients.length === 0)
        throw new Error("At least one recipient is required");
    const documentKey = randomBytes(32);
    const nonce = randomBytes(24);
    const id = createId("env");
    const channelId = input.channelId;
    const metadata = { id, channelId, senderId: input.sender.id };
    const ciphertext = xchacha20poly1305(documentKey, nonce, encoder.encode(canonicalJson(metadata))).encrypt(encoder.encode(canonicalJson(input.payload)));
    return SyncEnvelopeSchema.parse({
        protocolVersion: PROTOCOL_VERSION,
        id,
        channelId,
        senderId: input.sender.id,
        createdAt: input.now ?? new Date().toISOString(),
        expiresAt: input.expiresAt,
        nonce: bytesToBase64Url(nonce),
        ciphertext: bytesToBase64Url(ciphertext),
        recipients: input.recipients.map((recipient) => wrapDocumentKey(documentKey, recipient)),
    });
}
export function decryptEnvelope(envelope, identity) {
    const parsed = SyncEnvelopeSchema.parse(envelope);
    if (Date.parse(parsed.expiresAt) <= Date.now())
        throw new Error("Envelope expired");
    const recipient = parsed.recipients.find((entry) => entry.recipientId === identity.id);
    if (!recipient)
        throw new Error("Identity is not an envelope recipient");
    const documentKey = unwrapDocumentKey(recipient, identity);
    const metadata = { id: parsed.id, channelId: parsed.channelId, senderId: parsed.senderId };
    const plaintext = xchacha20poly1305(documentKey, base64UrlToBytes(parsed.nonce), encoder.encode(canonicalJson(metadata))).decrypt(base64UrlToBytes(parsed.ciphertext));
    return JSON.parse(decoder.decode(plaintext));
}
/** Firma con dominio esplicito: una revoca push non autorizza la consegna. */
export function createRecipientStatus(input) {
    const unsigned = {
        schemaVersion: 1,
        purpose: "recipient.status",
        channelId: input.channelId,
        subjectId: input.subjectId,
        active: input.active,
        changedAt: input.changedAt,
        issuerSigningKey: input.owner.signingPublicKey,
    };
    return { ...unsigned, signature: signValue(unsigned, input.owner.signingPrivateKey) };
}
/** Firma l'elenco che il proprietario considera completo in questo istante. */
export function createRecipientRoster(input) {
    const recipientIds = [...new Set(input.recipientIds)].sort();
    const unsigned = {
        schemaVersion: 1,
        purpose: "recipient.roster",
        channelId: input.channelId,
        recipientIds,
        changedAt: input.changedAt,
        issuerSigningKey: input.owner.signingPublicKey,
    };
    return { ...unsigned, signature: signValue(unsigned, input.owner.signingPrivateKey) };
}
